import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { ScrollToTop } from "@/components/ScrollToTop";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import pathFitness from "@/assets/icons/path-fitness.png";
import pathElite from "@/assets/icons/path-elite.png";
import pathFootball from "@/assets/icons/path-football.png";
import pathAthletic from "@/assets/icons/path-athletic.png";
import svcAthletic from "@/assets/icons/svc-athletic.png";
import svcEvaluation from "@/assets/icons/svc-evaluation.png";
import svcFat from "@/assets/icons/svc-fat.png";
import svcFootball from "@/assets/icons/svc-football.png";
import svcBodyweight from "@/assets/icons/svc-bodyweight.png";
import svcGym from "@/assets/icons/svc-gym.png";
import svcLactate from "@/assets/icons/svc-lactate.png";
import svcNutrition from "@/assets/icons/svc-nutrition.png";
import svcDiagnostic from "@/assets/icons/svc-diagnostic.png";
import svcPsychology from "@/assets/icons/svc-psychology.png";
import svcVo2Max from "@/assets/icons/svc-vo2max.png";
const logo = "/logo.webp";
const heroBg = "/hero-bg.webp";
import { LazyHeroVideo } from "@/components/LazyHeroVideo";

import { Questionnaire } from "@/components/Questionnaire";
import { ContactForm } from "@/components/ContactForm";
import { WelcomePopup } from "@/components/WelcomePopup";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SEO } from "@/components/SEO";
import { AvailabilityBadges } from "@/components/AvailabilityBadges";
import { GoalSelector } from "@/components/GoalSelector";
import { Testimonials } from "@/components/Testimonials";
import { SeoLongform } from "@/components/SeoLongform";
import iconMission from "@/assets/icons/who-mission.png";
import iconTeam from "@/assets/icons/who-team.png";
import iconResults from "@/assets/icons/who-results.png";
import iconScience from "@/assets/icons/who-science.png";
import iconPsychology from "@/assets/icons/who-psychology.png";

const Index = () => {
  const [questionnaireOpen, setQuestionnaireOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Valorform — Personal Training Munich | Performance Coaching"
        description="Science-based personal training & athletic testing in Munich. VO2 max, lactate, body composition, football & strength programs."
        path="/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": ["LocalBusiness", "HealthAndBeautyBusiness", "SportsActivityLocation"],
            "name": "Valorform",
            "alternateName": ["Valorform Munich", "Valorform Performance Training"],
            "url": "https://valorform.com/",
            "description": "Science-based personal training, performance coaching and athletic testing in Munich.",
            "image": "https://valorform.com/favicon.png",
            "logo": "https://valorform.com/favicon.png",
            "priceRange": "€35–€1599",
            "areaServed": [
              { "@type": "City", "name": "Munich" },
              { "@type": "AdministrativeArea", "name": "Bavaria" },
              { "@type": "Country", "name": "Germany" }
            ],
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Munich",
              "addressRegion": "Bavaria",
              "addressCountry": "DE"
            },
            "hasMap": "https://share.google/oCF1On11JGW3eh0e8",
            "sameAs": [
              "https://share.google/oCF1On11JGW3eh0e8"
            ],
            "keywords": "personal training Munich, performance coaching Munich, VO2 max test Munich, lactate test Munich, football training Munich, athletic training, fitness coach Munich, body composition analysis, sports nutrition, science-based training",
            "makesOffer": [
              { "@type": "Offer", "name": "Evaluation", "price": "69", "priceCurrency": "EUR", "url": "https://valorform.com/services/evaluation" },
              { "@type": "Offer", "name": "Lactate Test", "price": "129", "priceCurrency": "EUR", "url": "https://valorform.com/services/lactate-test" },
              { "@type": "Offer", "name": "VO2 Max Test", "price": "149", "priceCurrency": "EUR", "url": "https://valorform.com/services/vo2-max" },
              { "@type": "Offer", "name": "Fat Analysis", "price": "49", "priceCurrency": "EUR", "url": "https://valorform.com/services/fat-analysis" },
              { "@type": "Offer", "name": "Football Training", "price": "65", "priceCurrency": "EUR", "url": "https://valorform.com/services/football-training" },
              { "@type": "Offer", "name": "Nutrition Coaching", "priceCurrency": "EUR", "url": "https://valorform.com/services/nutrition" }
            ]
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Valorform",
            "url": "https://valorform.com/",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://valorform.com/?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              { "@type": "Question", "name": t("faq.q1.question"), "acceptedAnswer": { "@type": "Answer", "text": t("faq.q1.answer") } },
              { "@type": "Question", "name": t("faq.q2.question"), "acceptedAnswer": { "@type": "Answer", "text": t("faq.q2.answer") } },
              { "@type": "Question", "name": t("faq.q3.question"), "acceptedAnswer": { "@type": "Answer", "text": t("faq.q3.answer") } },
              { "@type": "Question", "name": t("faq.q4.question"), "acceptedAnswer": { "@type": "Answer", "text": t("faq.q4.answer") } },
              { "@type": "Question", "name": t("faq.q5.question"), "acceptedAnswer": { "@type": "Answer", "text": t("faq.q5.answer") } },
              { "@type": "Question", "name": t("faq.q6.question"), "acceptedAnswer": { "@type": "Answer", "text": t("faq.q6.answer") } },
              { "@type": "Question", "name": t("faq.q7.question"), "acceptedAnswer": { "@type": "Answer", "text": t("faq.q7.answer") } },
              { "@type": "Question", "name": t("faq.q8.question"), "acceptedAnswer": { "@type": "Answer", "text": t("faq.q8.answer") } }
            ]
          }
        ]}

      />
      <WelcomePopup />
      <Navigation />
      
      
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden mt-20">
        <LazyHeroVideo />


        <div className="relative z-10 text-center px-4">
          <img 
            src={logo} 
            alt="Valorform Logo" 
            fetchPriority="high"
            decoding="async"
            width={384}
            height={384}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 object-contain opacity-10 pointer-events-none mix-blend-lighten"
          />
          <h1 className="text-[clamp(1.35rem,6.2vw,2.75rem)] sm:text-5xl md:text-8xl leading-tight [hyphens:none] [-webkit-hyphens:none] [text-wrap:balance] [overflow-wrap:normal] [word-break:keep-all] max-w-[24ch] sm:max-w-none mx-auto font-bold text-foreground mb-6 animate-in fade-in duration-1000">
            {t('hero.title').split('\n').map((line, i) => (
              <span key={i}>
                {i > 0 ? <br className="hidden md:inline" /> : null}
                {i > 0 ? " " : null}
                {line}
              </span>
            ))}<br />{t('hero.subtitle')}
            <span className="sr-only"> — Science-Backed Personal Training and Performance Coaching in Munich (Personal Trainer München, Leistungsdiagnostik, Laktattest, VO2 max)</span>
          </h1>

          <Button 
            size="lg" 
            className="shadow-accent-glow text-lg px-8 py-6"
            onClick={() => setQuestionnaireOpen(true)}
          >
            {t('hero.cta')}
          </Button>
        </div>
      </section>

      {/* Primary performance goal selector — keyword-rich routes for personal training near me, youth football coaching in Munich, lactate threshold testing for runners */}
      <GoalSelector />

      {/* Who We Are Section */}
      <section id="who-are-we" className="relative py-20 px-4 scroll-mt-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <picture>
            <source srcSet="/hero-bg.webp" type="image/webp" />
            <img
              src={heroBg}
              alt="Valorform coaching team working with athletes in Munich"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover" width={1200} height={800} />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background"></div>
        </div>
        <div className="container mx-auto max-w-7xl relative z-10">
          <h2 className="text-5xl md:text-6xl font-bold text-center mb-4 text-foreground">
            {t('whoWeAre.title')}
          </h2>
          <p className="text-center text-lg text-muted-foreground mb-16 max-w-4xl mx-auto">
            {t('whoWeAre.description')}
          </p>
          
          <div className="relative z-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-5">

            {[
              {
                key: "mission",
                icon: iconMission,
                to: "/areas-of-expertise",
                card: "to-primary/20 hover:border-primary/70 hover:shadow-primary/20",
                glow: "from-primary/20",
                bar: "from-primary/80 via-primary to-primary/80",
                medallion: "from-primary/60 to-primary/25 rotate-3",
                title: "group-hover:text-primary",
                cta: "bg-primary text-primary-foreground",
              },
              {
                key: "team",
                icon: iconTeam,
                to: "/areas-of-expertise",
                card: "to-amber-500/20 hover:border-amber-500/70 hover:shadow-amber-500/20",
                glow: "from-amber-500/20",
                bar: "from-amber-500/80 via-amber-500 to-amber-500/80",
                medallion: "from-amber-500/60 to-amber-500/25 -rotate-3",
                title: "group-hover:text-amber-500",
                cta: "bg-amber-500 text-background",
              },
              {
                key: "results",
                icon: iconResults,
                to: "/reviews",
                card: "to-emerald-500/20 hover:border-emerald-500/70 hover:shadow-emerald-500/20",
                glow: "from-emerald-500/20",
                bar: "from-emerald-500/80 via-emerald-500 to-emerald-500/80",
                medallion: "from-emerald-500/60 to-emerald-500/25 rotate-3",
                title: "group-hover:text-emerald-500",
                cta: "bg-emerald-500 text-background",
              },
              {
                key: "science",
                icon: iconScience,
                to: "/services/performance-diagnostic",
                card: "to-blue-500/20 hover:border-blue-500/70 hover:shadow-blue-500/20",
                glow: "from-blue-500/20",
                bar: "from-blue-500/80 via-blue-500 to-blue-500/80",
                medallion: "from-blue-500/60 to-blue-500/25 -rotate-3",
                title: "group-hover:text-blue-500",
                cta: "bg-blue-500 text-background",
              },
              {
                key: "psychology",
                icon: iconPsychology,
                to: "/services/psychology",
                card: "to-purple-500/20 hover:border-purple-500/70 hover:shadow-purple-500/20",
                glow: "from-purple-500/20",
                bar: "from-purple-500/80 via-purple-500 to-purple-500/80",
                medallion: "from-purple-500/60 to-purple-500/25 rotate-3",
                title: "group-hover:text-purple-500",
                cta: "bg-purple-500 text-background",
              },
            ].map((item) => (
              <Card
                key={item.key}
                className={`group bg-gradient-to-br from-card via-card/80 ${item.card} border-border/30 transition-all duration-500 hover:shadow-xl hover:-translate-y-1 overflow-hidden relative`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${item.glow} to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${item.bar}`} />
                <CardContent className="p-5 lg:p-4 xl:p-6 text-center relative z-10 flex flex-col h-full">
                  <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${item.medallion} flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 group-hover:rotate-0 transition-transform duration-300`}>
                    <img
                      src={item.icon}
                      alt=""
                      aria-hidden="true"
                      loading="lazy"
                      decoding="async"
                      width={512}
                      height={512}
                      className="w-12 h-12 object-contain [filter:brightness(0)_invert(1)]"
                    />
                  </div>
                  <h3 className={`text-base lg:text-lg xl:text-xl font-bold mb-3 text-foreground text-center leading-tight [hyphens:none] [overflow-wrap:normal] [word-break:keep-all] ${item.title} transition-colors`}>
                    {t(`whoWeAre.${item.key}.title`)}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    {t(`whoWeAre.${item.key}.text`)}
                  </p>
                  <Link
                    to={item.to}
                    className={`mt-auto inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-transform duration-300 group-hover:scale-[1.03] ${item.cta}`}
                  >
                    {t("goalSelector.cta")}
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-16 max-w-4xl mx-auto text-center px-2">
            <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4 [hyphens:none] [-webkit-hyphens:none] [overflow-wrap:break-word]">{t('whoWeAre.aboutHeading')}</h3>
            <p className="text-muted-foreground leading-relaxed text-sm md:text-lg [hyphens:none] [-webkit-hyphens:none] [overflow-wrap:break-word] text-left md:text-center">
              {t('whoWeAre.aboutBody')}
            </p>
          </div>
        </div>
      </section>

      {/* Choose Your Path Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-5xl md:text-6xl font-bold text-center mb-4 text-foreground">
            {t('choosePath.title')}
          </h2>
          <p className="text-center text-lg text-muted-foreground mb-16">
            {t('choosePath.description')}
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
            {([
              {
                key: "fitnessEnthusiast",
                art: pathFitness,
                color: "var(--path-fitness)",
                to: "/categories/normal-people#pricing",
                inPerson: false,
                featured: false,
                title: t("choosePath.fitnessEnthusiast.title"),
                description: t("choosePath.fitnessEnthusiast.description"),
                points: [
                  t("choosePath.fitnessEnthusiast.point1"),
                  t("choosePath.fitnessEnthusiast.point2"),
                  t("choosePath.fitnessEnthusiast.point3"),
                ],
              },
              {
                key: "eliteAthletes",
                art: pathElite,
                color: "var(--path-elite)",
                to: "/categories/elite-athletes#pricing",
                inPerson: false,
                featured: true,
                title: t("choosePath.eliteAthletes.title"),
                description: t("choosePath.eliteAthletes.description"),
                points: [
                  t("choosePath.eliteAthletes.point1"),
                  t("choosePath.eliteAthletes.point2"),
                  t("choosePath.eliteAthletes.point3"),
                ],
              },
              {
                key: "footballTraining",
                art: pathFootball,
                color: "var(--svc-football)",
                to: "/categories/football-training#pricing",
                inPerson: true,
                featured: false,
                title: t("choosePath.footballTraining.title"),
                description: t("choosePath.footballTraining.description"),
                points: [
                  t("choosePath.footballTraining.point1"),
                  t("choosePath.footballTraining.point2"),
                  t("choosePath.footballTraining.point3"),
                ],
              },
              {
                key: "athleticTraining",
                art: pathAthletic,
                color: "var(--svc-athletic)",
                to: "/categories/athletic-training#pricing",
                inPerson: true,
                featured: false,
                title: t("choosePath.athleticTraining.title"),
                description: t("choosePath.athleticTraining.description"),
                points: [
                  t("choosePath.athleticTraining.point1"),
                  t("choosePath.athleticTraining.point2"),
                  t("choosePath.athleticTraining.point3"),
                ],
              },
            ] as const).map(({ key, art, color, to, inPerson, featured, title, description, points }) => (
              <div
                key={key}
                className="group relative pt-6"
                style={{ ["--path" as string]: color }}
              >
                {/* colored folder shell */}
                <span
                  aria-hidden="true"
                  className="absolute inset-x-0 top-0 bottom-0 translate-x-2 translate-y-2 rounded-2xl transition-transform duration-300 group-hover:translate-x-2.5 group-hover:translate-y-2.5"
                  style={{ backgroundColor: "hsl(var(--path))" }}
                />
                {/* folder tab */}
                <span
                  aria-hidden="true"
                  className="absolute left-4 top-0 h-9 w-2/5 rounded-t-xl"
                  style={{ backgroundColor: "hsl(var(--path))" }}
                />

                <div
                  className="relative flex h-full flex-col rounded-2xl border border-white/5 p-6 transition-transform duration-300 group-hover:-translate-y-1"
                  style={{
                    backgroundColor: "hsl(0 0% 11%)",
                    boxShadow: featured
                      ? "0 0 60px -10px hsl(var(--path) / 0.55), inset 0 0 80px -30px hsl(var(--path) / 0.75)"
                      : "0 24px 50px -26px hsl(var(--path) / 0.5), inset 0 1px 0 hsl(0 0% 100% / 0.05)",
                  }}
                >
                  <img
                    src={art}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    decoding="async"
                    width={512}
                    height={512}
                    className="mx-auto mb-5 h-16 w-16 object-contain [filter:brightness(0)_invert(1)]"
                  />

                  <h3 className="text-2xl xl:text-3xl font-bold text-center text-foreground leading-tight mb-3 [hyphens:none] [overflow-wrap:break-word] [word-break:normal]">
                    {title}
                  </h3>

                  {inPerson && (
                    <div className="flex justify-center mb-3">
                      <AvailabilityBadges inPerson />
                    </div>
                  )}

                  <p className="text-center text-sm text-muted-foreground mb-6 [hyphens:none]">
                    {description}
                  </p>

                  <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">
                    {t("choosePath.whoThisIsFor")}
                  </h4>
                  <ul className="space-y-2.5 text-sm text-muted-foreground mb-8 flex-grow">
                    {points.map((point) => (
                      <li key={point} className="flex items-start [hyphens:none]">
                        <span className="mr-2 leading-6" style={{ color: "hsl(var(--path))" }}>
                          •
                        </span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={to}
                    className="mt-auto inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-bold transition-transform duration-300 group-hover:scale-[1.02]"
                    style={{ backgroundColor: "hsl(var(--path))", color: "hsl(0 0% 8%)" }}
                  >
                    {t("choosePath.viewPlans")}
                    {featured && <ArrowRight className="h-4 w-4" aria-hidden="true" />}
                  </Link>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-20 px-4" style={{ background: 'hsl(0 0% 8%)' }}>
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-5xl md:text-6xl font-bold text-center mb-4 text-foreground">
            {t('whatWeDo.title')}
          </h2>
          <p className="text-center text-lg text-muted-foreground mb-16">
            {t('whatWeDo.description')}
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {([
              { path: "/categories/athletic-training", art: svcAthletic, titleKey: "whatWeDo.athleticTraining.title", descKey: "whatWeDo.athleticTraining.description", color: "var(--svc-athletic)" },
              { path: "/services/evaluation", art: svcEvaluation, titleKey: "whatWeDo.evaluation.title", descKey: "whatWeDo.evaluation.description", color: "var(--svc-evaluation)" },
              { path: "/services/fat-analysis", art: svcFat, titleKey: "whatWeDo.fatAnalysis.title", descKey: "whatWeDo.fatAnalysis.description", color: "var(--svc-fat)" },
              { path: "/services/football-training", art: svcFootball, titleKey: "whatWeDo.footballTraining.title", descKey: "whatWeDo.footballTraining.description", color: "var(--svc-football)" },
              { path: "/services/full-body-weight", art: svcBodyweight, titleKey: "whatWeDo.fullBodyWeight.title", descKey: "whatWeDo.fullBodyWeight.description", color: "var(--svc-bodyweight)" },
              { path: "/services/gym-training", art: svcGym, titleKey: "whatWeDo.gymTraining.title", descKey: "whatWeDo.gymTraining.description", color: "var(--svc-gym)" },
              { path: "/services/lactate-test", art: svcLactate, titleKey: "whatWeDo.lactateTest.title", descKey: "whatWeDo.lactateTest.description", color: "var(--svc-lactate)" },
              { path: "/services/nutrition", art: svcNutrition, titleKey: "whatWeDo.nutrition.title", descKey: "whatWeDo.nutrition.description", color: "var(--svc-nutrition)" },
              { path: "/services/performance-diagnostic", art: svcDiagnostic, titleKey: "whatWeDo.performanceDiagnostic.title", descKey: "whatWeDo.performanceDiagnostic.description", color: "var(--svc-diagnostic)" },
              { path: "/services/psychology", art: svcPsychology, titleKey: "whatWeDo.psychology.title", descKey: "whatWeDo.psychology.description", color: "var(--svc-psychology)" },
              { path: "/services/vo2-max", art: svcVo2Max, titleKey: "whatWeDo.vo2Max.title", descKey: "whatWeDo.vo2Max.description", color: "var(--svc-vo2max)" },
            ] as Array<{ path: string; art: string; color: string; title?: string; description?: string; titleKey?: string; descKey?: string }>)
              .map((s) => ({ ...s, _name: (s.title ?? t(s.titleKey!)) as string, _desc: (s.description ?? t(s.descKey!)) as string }))
              .sort((a, b) => a._name.localeCompare(b._name))
              .map((s) => {
                return (
                  <div
                    key={s.path}
                    className="group relative pt-5"
                    style={{ ["--svc" as string]: s.color, ["--card-accent" as string]: s.color }}
                  >
                    {/* folder tab */}
                    <span
                      aria-hidden="true"
                      className="absolute left-6 top-0 h-8 w-2/5 rounded-t-xl border-2 border-b-0"
                      style={{ borderColor: "hsl(var(--svc))", backgroundColor: "hsl(0 0% 11%)" }}
                    />
                    <Link
                      to={s.path}
                      onClick={() => window.scrollTo(0, 0)}
                      className="accent-card-link block h-full focus-visible:outline-none"
                    >
                      <div
                        className="accent-card relative flex h-full flex-col rounded-2xl border-2 p-6 text-center"
                        style={{
                          borderColor: "hsl(var(--svc))",
                          backgroundColor: "hsl(0 0% 11%)",
                        }}
                      >
                      <span
                        className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-2xl border border-white/10"
                        style={{ backgroundColor: "hsl(0 0% 16%)" }}
                      >
                        <img
                          src={s.art}
                          alt=""
                          aria-hidden="true"
                          loading="lazy"
                          decoding="async"
                          width={512}
                          height={512}
                          className="h-16 w-16 object-contain [filter:brightness(0)_invert(1)_drop-shadow(0_1px_2px_rgba(0,0,0,0.6))]"
                        />
                      </span>
                      <h3
                        className="accent-title text-2xl font-bold mb-3 leading-tight [hyphens:none] [overflow-wrap:break-word] [word-break:normal]"
                        style={{ color: "hsl(var(--svc))" }}
                      >
                        {s._name}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-6 [hyphens:none]">{s._desc}</p>
                      <span
                        className="accent-cta mt-auto inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-bold"
                        style={{ backgroundColor: "hsl(var(--svc))", color: "hsl(0 0% 8%)" }}
                      >
                        {t("whatWeDo.viewService", { defaultValue: "View Service" })}
                        <ArrowRight className="h-4 w-4" aria-hidden="true" />
                      </span>
                      </div>
                    </Link>

                  </div>
                );
              })}
          </div>

        </div>
      </section>

      {/* Testimonials — placeholder cards; replace via i18n once final copy lands */}
      <Testimonials />

      {/* FAQs Section */}
      <section className="py-20 px-4 bg-[hsl(0,0%,8%)]">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-4">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="text-4xl">🤔</span>
              <h2 className="text-4xl md:text-5xl font-bold text-foreground">{t('faq.title')}</h2>
            </div>
            <p className="text-muted-foreground text-lg">{t('faq.description')}</p>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4 mt-12">
            <AccordionItem value="item-1" className="bg-card border border-border rounded-lg px-6">
              <AccordionTrigger className="text-foreground hover:text-primary text-left">{t('faq.q1.question')}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{t('faq.q1.answer')}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2" className="bg-card border border-border rounded-lg px-6">
              <AccordionTrigger className="text-foreground hover:text-primary text-left">{t('faq.q2.question')}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{t('faq.q2.answer')}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3" className="bg-card border border-border rounded-lg px-6">
              <AccordionTrigger className="text-foreground hover:text-primary text-left">{t('faq.q3.question')}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{t('faq.q3.answer')}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-4" className="bg-card border border-border rounded-lg px-6">
              <AccordionTrigger className="text-foreground hover:text-primary text-left">{t('faq.q4.question')}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{t('faq.q4.answer')}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-5" className="bg-card border border-border rounded-lg px-6">
              <AccordionTrigger className="text-foreground hover:text-primary text-left">{t('faq.q5.question')}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{t('faq.q5.answer')}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-6" className="bg-card border border-border rounded-lg px-6">
              <AccordionTrigger className="text-foreground hover:text-primary text-left">{t('faq.q6.question')}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{t('faq.q6.answer')}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-7" className="bg-card border border-border rounded-lg px-6">
              <AccordionTrigger className="text-foreground hover:text-primary text-left">{t('faq.q7.question')}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{t('faq.q7.answer')}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-8" className="bg-card border border-border rounded-lg px-6">
              <AccordionTrigger className="text-foreground hover:text-primary text-left">{t('faq.q8.question')}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{t('faq.q8.answer')}</AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-4 bg-card-gradient">
        <div className="container mx-auto max-w-3xl">
          <div className="flex items-center justify-center gap-3 mb-12">
            <span className="text-4xl">📡</span>
            <h2 className="text-4xl md:text-5xl font-bold text-foreground">{t('contact.title')}</h2>
          </div>
          <Card className="bg-card border-border">
            <CardContent className="p-8">
              <ContactForm showCategorySelection={true} />
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Internal-linking module — pushes homepage ranking power directly into revenue keywords */}
      <section
        aria-label={t("exploreServices.aria")}
        className="py-12 px-4 border-t border-border/50"
        style={{ contentVisibility: "auto", containIntrinsicSize: "400px" } as React.CSSProperties}
      >
        <div className="container mx-auto max-w-5xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">{t("exploreServices.heading")}</h2>
          <nav aria-label={t("exploreServices.ariaNav")} className="flex flex-wrap justify-center gap-x-6 gap-y-3 text-base">
            <Link to="/services/nutrition" className="text-primary hover:underline underline-offset-4 font-medium">{t("exploreServices.nutrition")}</Link>
            <span className="text-muted-foreground" aria-hidden="true">·</span>
            <Link to="/services/vo2-max" className="text-primary hover:underline underline-offset-4 font-medium">{t("exploreServices.vo2")}</Link>
            <span className="text-muted-foreground" aria-hidden="true">·</span>
            <Link to="/services/gym-training" className="text-primary hover:underline underline-offset-4 font-medium">{t("exploreServices.gym")}</Link>
            <span className="text-muted-foreground" aria-hidden="true">·</span>
            <Link to="/services/lactate-test" className="text-primary hover:underline underline-offset-4 font-medium">{t("exploreServices.lactate")}</Link>
            <span className="text-muted-foreground" aria-hidden="true">·</span>
            <Link to="/services/football-training" className="text-primary hover:underline underline-offset-4 font-medium">{t("exploreServices.football")}</Link>
            <span className="text-muted-foreground" aria-hidden="true">·</span>
            <Link to="/services/evaluation" className="text-primary hover:underline underline-offset-4 font-medium">{t("exploreServices.evaluation")}</Link>
            <span className="text-muted-foreground" aria-hidden="true">·</span>
            <Link to="/services/fat-analysis" className="text-primary hover:underline underline-offset-4 font-medium">{t("exploreServices.fat")}</Link>
            <span className="text-muted-foreground" aria-hidden="true">·</span>
            <Link to="/services/psychology" className="text-primary hover:underline underline-offset-4 font-medium">{t("exploreServices.psychology")}</Link>
          </nav>
          <p className="text-xs text-muted-foreground mt-6">
            {t("exploreServices.localHeader")}{" "}
            <Link to="/leistungsdiagnostik-trudering" className="underline hover:text-primary">{t("footer.localOffers.trudering")}</Link>{" · "}
            <Link to="/vo2max-test-riem" className="underline hover:text-primary">{t("footer.localOffers.vo2Riem")}</Link>{" · "}
            <Link to="/personal-trainer-muenchen-ost" className="underline hover:text-primary">{t("footer.localOffers.trainerEast")}</Link>

          </p>
        </div>
      </section>

      <SeoLongform ns="home" />

      <Questionnaire open={questionnaireOpen} onOpenChange={setQuestionnaireOpen} />
      <ScrollToTop />
      <Footer />
    </div>
  );
};

export default Index;
